import { watch } from 'vue'
import { useCookieConsent } from '@/composables/useCookieConsent'
import { isMarketingHost } from '@/utils/platformHost'

const SCRIPT_ID = 'openai-ads-pixel'
const PIXEL_ID = (import.meta.env.VITE_OPENAI_ADS_PIXEL_ID as string | undefined) ?? ''

interface OaiqFn {
  (...args: unknown[]): void
  q: unknown[][]
}
interface OaiqWindow extends Window {
  oaiq?: OaiqFn
}

function ensureOaiq(): OaiqFn {
  const win = window as OaiqWindow
  if (win.oaiq) return win.oaiq
  const oaiq = ((...args: unknown[]) => {
    oaiq.q.push(args)
  }) as OaiqFn
  oaiq.q = []
  win.oaiq = oaiq
  return oaiq
}

let initialized = false
let lastConsent = false

function grantConsent(): OaiqFn {
  const oaiq = ensureOaiq()
  if (!initialized) {
    initialized = true
    // This function is reachable only after explicit marketing consent. Set
    // consent before init without first sending false: OpenAI documents that
    // false clears stored attribution cookies, including a prior ad click.
    oaiq('consent', true)
    lastConsent = true
    oaiq('init', { pixelId: PIXEL_ID, debug: import.meta.env.DEV })
    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script')
      script.id = SCRIPT_ID
      script.async = true
      script.src = 'https://bzrcdn.openai.com/sdk/oaiq.min.js'
      document.head.appendChild(script)
    }
  }
  if (!lastConsent) oaiq('consent', true)
  lastConsent = true
  return oaiq
}

/** Called only after a new business signup is successfully verified. */
export function reportSignupConversion(): boolean {
  if (!PIXEL_ID || !isMarketingHost()) return false
  const { consent } = useCookieConsent()
  // Never queue denied events to replay after a later acceptance.
  if (consent.value !== 'granted') return false
  grantConsent()('measure', 'registration_completed', { type: 'customer_action' })
  return true
}

export function useOpenAIAdsPixel(): void {
  if (!PIXEL_ID || !isMarketingHost()) return
  const { consent } = useCookieConsent()
  watch(
    consent,
    (value) => {
      if (value === 'granted') grantConsent()
      else if (initialized && lastConsent) {
        ensureOaiq()('consent', false)
        lastConsent = false
      }
    },
    // Revoke synchronously, including when the SDK is still downloading.
    { immediate: true, flush: 'sync' },
  )
}
